import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Lock, Phone, Mail, MapPin, Image as ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "fr_admin_token";

const statusColors = {
  new: "bg-rust text-cream",
  contacted: "bg-gold text-charcoal",
  closed: "bg-charcoal text-cream",
};

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(STORAGE_KEY) || "");
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const fetchLeads = useCallback(
    async (tk) => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/leads`, {
          headers: { "x-admin-token": tk },
        });
        setLeads(res.data.leads || []);
      } catch (err) {
        toast.error("Could not load leads.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        await axios.post(`${API}/admin/verify`, {}, { headers: { "x-admin-token": token } });
        setAuthed(true);
        fetchLeads(token);
      } catch {
        localStorage.removeItem(STORAGE_KEY);
        setToken("");
      }
    })();
  }, [token, fetchLeads]);

  const login = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "x-admin-token": input } });
      localStorage.setItem(STORAGE_KEY, input);
      setToken(input);
      toast.success("Welcome back!");
    } catch {
      toast.error("Invalid admin key.");
    } finally {
      setVerifying(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setAuthed(false);
    setLeads([]);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/leads/${id}`,
        { status },
        { headers: { "x-admin-token": token } }
      );
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Status updated.");
    } catch {
      toast.error("Could not update status.");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-charcoal flex items-center justify-center px-4">
        <Toaster position="top-center" richColors />
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="bg-cream border border-charcoal/20 shadow-md p-8 w-full max-w-md"
        >
          <span className="flex items-center justify-center w-14 h-14 bg-rust text-cream border border-charcoal/20 mb-5">
            <Lock className="w-7 h-7" />
          </span>
          <h1 className="font-heading text-4xl uppercase text-charcoal">Admin Login</h1>
          <p className="text-charcoal/70 mt-2 text-sm">
            Future Roofing and Repair — lead dashboard. Enter your admin key to continue.
          </p>
          <Input
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Admin key"
            data-testid="admin-key-input"
            className="border border-charcoal/20 rounded-none bg-cream mt-5 h-11"
          />
          <button
            type="submit"
            disabled={verifying}
            data-testid="admin-login-btn"
            className="mt-4 w-full flex items-center justify-center gap-2 bg-rust text-cream border border-charcoal/20 shadow-md font-heading text-xl uppercase py-3 disabled:opacity-70"
          >
            {verifying ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enter Dashboard"}
          </button>
          <Link to="/" className="block text-center mt-4 text-sm text-charcoal/60 hover:text-rust">
            ← Back to website
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <Toaster position="top-center" richColors />
      <header className="bg-charcoal text-cream border-b border-charcoal/15 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div>
            <p className="font-heading text-2xl uppercase">Lead Dashboard</p>
            <p className="font-accent text-xs uppercase tracking-widest text-gold">
              Future Roofing and Repair
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchLeads(token)}
              data-testid="admin-refresh"
              className="flex items-center gap-2 border border-cream/40 px-3 py-2 font-accent uppercase text-xs tracking-widest hover:bg-cream hover:text-charcoal transition-colors"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="flex items-center gap-2 bg-rust border border-cream/40 px-3 py-2 font-accent uppercase text-xs tracking-widest"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-3xl uppercase text-charcoal">
            {leads.length} Lead{leads.length !== 1 ? "s" : ""}
          </h2>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-charcoal/70 py-20 justify-center">
            <Loader2 className="w-6 h-6 animate-spin" /> Loading leads…
          </div>
        ) : leads.length === 0 ? (
          <div className="border border-dashed border-charcoal/30 p-16 text-center text-charcoal/60">
            No leads yet. Submissions from the website forms will appear here.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="admin-leads">
            {leads.map((lead) => (
              <div
                key={lead.id}
                className="bg-cream border border-charcoal/20 shadow-md p-6"
                data-testid={`admin-lead-${lead.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-heading text-2xl uppercase text-charcoal leading-none">
                      {lead.name}
                    </h3>
                    <p className="text-xs text-charcoal/50 mt-1">
                      {new Date(lead.created_at).toLocaleString()}
                    </p>
                  </div>
                  <span
                    className={`font-accent uppercase text-[10px] tracking-widest px-2 py-1 border border-charcoal/20 ${
                      statusColors[lead.status] || "bg-cream-200 text-charcoal"
                    }`}
                  >
                    {lead.status}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-charcoal hover:text-rust">
                    <Phone className="w-4 h-4 text-rust" /> {lead.phone}
                  </a>
                  <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-charcoal hover:text-rust break-all">
                    <Mail className="w-4 h-4 text-rust" /> {lead.email}
                  </a>
                  {lead.property_address && (
                    <span className="flex items-center gap-2 text-charcoal/80 sm:col-span-2">
                      <MapPin className="w-4 h-4 text-rust" /> {lead.property_address}
                    </span>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap gap-2 text-xs font-accent uppercase tracking-wide">
                  <span className="border border-charcoal px-2 py-0.5">{lead.property_type}</span>
                  {lead.service_type && (
                    <span className="border border-charcoal px-2 py-0.5">{lead.service_type}</span>
                  )}
                  {lead.preferred_contact && (
                    <span className="border border-charcoal px-2 py-0.5">Contact: {lead.preferred_contact}</span>
                  )}
                  {lead.preferred_time && (
                    <span className="border border-charcoal px-2 py-0.5">{lead.preferred_time}</span>
                  )}
                </div>

                {lead.description && (
                  <p className="mt-3 text-sm text-charcoal/80 bg-cream-200 border border-charcoal/30 p-3">
                    {lead.description}
                  </p>
                )}

                {lead.photos && lead.photos.length > 0 && (
                  <div className="mt-3">
                    <p className="flex items-center gap-1.5 text-xs font-accent uppercase tracking-widest text-charcoal/60 mb-2">
                      <ImageIcon className="w-4 h-4" /> {lead.photos.length} Photo(s)
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {lead.photos.map((ph) => (
                        <a
                          key={ph.id}
                          href={`${API}/files/${ph.storage_path}?token=${encodeURIComponent(token)}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <img
                            src={`${API}/files/${ph.storage_path}?token=${encodeURIComponent(token)}`}
                            alt={ph.original_filename}
                            className="w-16 h-16 object-cover border border-charcoal/20"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4 flex items-center gap-2">
                  <span className="font-accent uppercase text-xs tracking-widest text-charcoal/60">
                    Status:
                  </span>
                  <Select value={lead.status} onValueChange={(v) => updateStatus(lead.id, v)}>
                    <SelectTrigger
                      className="border border-charcoal/20 rounded-none bg-cream h-9 w-40"
                      data-testid={`admin-status-${lead.id}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
